import { Link } from 'react-router-dom';

import { useBusinessAuth } from '../../context/BusinessAuthContext';

const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus';

// Sections of the workspace the sidebar already selected. They are children of
// that workspace, never a second copy of the sidebar's own destinations. Some
// sections are their own page (proofs, the settlement desk); they carry the
// same row so moving between them keeps the reader inside the workspace.
export function SectionNav({ ariaLabel, sections, current, links = [] }) {
  if (sections.length < 2 && links.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b border-neutral-200 dark:border-neutral-800">
      {sections.length > 1 ? (
        <nav aria-label={ariaLabel} className="-mb-px flex max-w-full gap-1 overflow-x-auto">
          {sections.map(({ key, title, to }) => {
            const active = current === key;
            return (
              <Link key={key} to={to} aria-current={active ? 'page' : undefined}
                className={`inline-flex min-h-11 shrink-0 items-center border-b-2 px-3 text-sm font-semibold ${focusRing} ${active
                  ? 'border-accent text-accent'
                  : 'border-transparent text-neutral-600 hover:text-neutral-950 dark:text-neutral-400 dark:hover:text-white'}`}>
                {title}
              </Link>
            );
          })}
        </nav>
      ) : <span />}
      {links.length > 0 && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 pb-2 text-sm">
          {links.map(({ to, title }) => <Link key={to} to={to} className={`inline-flex min-h-9 items-center font-semibold underline underline-offset-4 ${focusRing}`}>{title}</Link>)}
        </div>
      )}
    </div>
  );
}

function useCan() {
  const auth = useBusinessAuth();
  const owner = String(auth?.role || '').toUpperCase() === 'OWNER';
  return (permission) => owner || Boolean(auth?.hasPermission?.(permission));
}

// Physical delivery work, including the record of what was delivered (proofs)
// and the counter where a rider hands in the trip's cash. Finance keeps its own
// COD custody page; the link says so rather than moving it.
export function DeliverySections({ current }) {
  const can = useCan();
  const board = can('delivery.dispatch.read.business');
  const returns = can('delivery.custody.receive.business') || can('delivery.returns.disposition.business')
    || can('delivery.exceptions.manage.business');
  const sections = [
    board && { key: 'trips', title: 'Trips', to: '/business/delivery?view=trips' },
    board && { key: 'parcels', title: 'Parcels', to: '/business/delivery?view=parcels' },
    can('delivery.riders.read.business') && { key: 'riders', title: 'Riders', to: '/business/delivery?view=riders' },
    returns && { key: 'returns', title: 'Rider returns', to: '/business/delivery?view=returns' },
    can('delivery.proofs.review.business') && { key: 'proofs', title: 'Delivery proofs', to: '/business/delivery/proofs?show=all' },
    can('delivery.cod.remit.business') && { key: 'settlement', title: 'Settlement desk', to: '/business/delivery/settlement' },
  ].filter(Boolean);
  const links = [
    can('delivery.cod.read.business') && { to: '/business/finance/cod', title: 'COD custody in Finance' },
  ].filter(Boolean);
  return <SectionNav ariaLabel="Delivery sections" sections={sections} current={current} links={links} />;
}

// Recovery work: decisions first, then the signals and reviews beside them.
export function ExceptionSections({ current }) {
  const can = useCan();
  const exceptions = can('delivery.exceptions.manage.business');
  const sections = [
    (exceptions || can('delivery.assignment_problems.manage.business')) && { key: 'issues', title: 'Decisions', to: '/business/delivery?view=issues' },
    exceptions && { key: 'reports', title: 'Road reports', to: '/business/delivery?view=reports' },
    can('delivery.proofs.review.business') && { key: 'proofs', title: 'Flagged proofs', to: '/business/delivery/proofs' },
    exceptions && { key: 'custody', title: 'Custody reviews', to: '/business/orders/custody-reviews' },
  ].filter(Boolean);
  return <SectionNav ariaLabel="Exception sections" sections={sections} current={current} />;
}
